/**
 * TourSpotlight.jsx — moteur visuel du tour guidé "Ultra-pro" : découpe lumineuse (spotlight)
 * sur le vrai élément de la page (technique box-shadow, pas de clone/capture d'écran) + carte
 * d'explication glassmorphism. Pilote la navigation inter-écrans via `tourNav` avant de
 * localiser l'élément cible (poll + `scrollIntoView`).
 */
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Sparkles } from 'lucide-react';
import { useTour } from '../../context/TourContext';
import { tourNav } from '../../utils/tourNav';

const PAD = 10;
const CARD_W = 320;

const waitFor = (fn, { attempts = 30, interval = 150 } = {}) => new Promise((resolve) => {
  let tries = 0;
  const tick = () => {
    const v = fn();
    if (v) { resolve(v); return; }
    tries += 1;
    if (tries >= attempts) { resolve(null); return; }
    setTimeout(tick, interval);
  };
  tick();
});

export const TourSpotlight = ({ en }) => {
  const tour = useTour();
  const [rect, setRect] = useState(null);
  const [cardPos, setCardPos] = useState({ x: 16, y: 16, side: 'bottom' });
  const [neverShow, setNeverShow] = useState(false);
  const runId = useRef(0);

  const computeCardPos = useCallback((r) => {
    const vw = window.innerWidth, vh = window.innerHeight;
    const cardH = 210;
    const spaceBelow = vh - r.bottom - PAD * 2;
    const spaceAbove = r.top - PAD * 2;
    let side = 'bottom', y;
    if (spaceBelow >= cardH || spaceBelow >= spaceAbove) { side = 'bottom'; y = r.bottom + PAD + 8; }
    else { side = 'top'; y = r.top - PAD - 8 - cardH; }
    y = Math.max(12, Math.min(y, vh - cardH - 12));
    let x = r.left + r.width / 2 - CARD_W / 2;
    x = Math.max(12, Math.min(x, vw - CARD_W - 12));
    return { x, y, side };
  }, []);

  useEffect(() => {
    const id = ++runId.current;
    if (!tour?.active || !tour.step) { setRect(null); return; }
    setNeverShow(tour.isNeverShow());
    const step = tour.step;

    (async () => {
      for (const action of step.nav || []) {
        const setter = await waitFor(() => tourNav[action.fn], { attempts: 20, interval: 150 });
        if (runId.current !== id) return;
        if (setter) setter(action.arg);
        await new Promise((r) => setTimeout(r, 180));
      }
      const el = await waitFor(() => document.querySelector(`[data-testid="${step.selector}"]`), { attempts: 26, interval: 170 });
      if (runId.current !== id) return;
      if (!el) {
        // Élément introuvable (ex: bannière conditionnelle absente pour ce client) → on saute
        // silencieusement à l'étape suivante plutôt que d'afficher un écran noir bloquant.
        tour.next();
        return;
      }
      el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      await new Promise((r) => setTimeout(r, 380));
      if (runId.current !== id) return;
      const r0 = el.getBoundingClientRect();
      const r = { left: r0.left - PAD, top: r0.top - PAD, width: r0.width + PAD * 2, height: r0.height + PAD * 2, bottom: r0.bottom + PAD, right: r0.right + PAD };
      setRect(r);
      setCardPos(computeCardPos(r));
    })();

    return () => { runId.current += 1; };
  }, [tour?.active, tour?.stepIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!tour?.active) return null;

  const L = (fr, eng) => (en ? eng : fr);
  const { step, stepIndex, total, next, prev, skip } = tour;

  return createPortal(
    <div className="fixed inset-0 z-[300]" data-testid="tour-spotlight-overlay">
      <div className="fixed inset-0" onClick={(e) => e.stopPropagation()} />
      <AnimatePresence>
        {rect && (
          <motion.div
            key="cutout"
            className="fixed rounded-2xl border-2 border-primary pointer-events-none"
            style={{ boxShadow: '0 0 0 9999px rgba(0,0,0,0.86)' }}
            initial={{ left: rect.left, top: rect.top, width: rect.width, height: rect.height, opacity: 0 }}
            animate={{
              left: rect.left, top: rect.top, width: rect.width, height: rect.height, opacity: 1,
              boxShadow: [
                '0 0 0 9999px rgba(0,0,0,0.86), 0 0 15px rgba(255,23,68,0.35)',
                '0 0 0 9999px rgba(0,0,0,0.86), 0 0 34px rgba(255,23,68,0.65)',
                '0 0 0 9999px rgba(0,0,0,0.86), 0 0 15px rgba(255,23,68,0.35)',
              ],
            }}
            transition={{ left: { type: 'spring', damping: 25, stiffness: 200, mass: 0.5 }, top: { type: 'spring', damping: 25, stiffness: 200, mass: 0.5 }, width: { type: 'spring', damping: 25, stiffness: 200, mass: 0.5 }, height: { type: 'spring', damping: 25, stiffness: 200, mass: 0.5 }, boxShadow: { duration: 2, repeat: Infinity } }}
          />
        )}
      </AnimatePresence>
      {!rect && <div className="fixed inset-0 bg-black/86" />}

      <AnimatePresence mode="wait">
        <motion.div
          key={step.id}
          initial={{ opacity: 0, y: 10, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 10, scale: 0.95 }}
          transition={{ duration: 0.2 }}
          style={{ position: 'fixed', left: rect ? cardPos.x : '50%', top: rect ? cardPos.y : '50%', transform: rect ? 'none' : 'translate(-50%,-50%)' }}
          className="w-80 max-w-[calc(100vw-24px)]"
          data-testid="tour-card"
        >
          <div className="relative bg-gradient-to-br from-zinc-900 to-zinc-950 border border-white/10 rounded-2xl p-5 shadow-2xl overflow-hidden">
            <div className="absolute -top-10 -right-10 w-32 h-32 bg-primary/25 rounded-full blur-3xl pointer-events-none" />
            <div className="relative flex items-center justify-between mb-3">
              <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-primary uppercase tracking-wide" aria-live="polite" data-testid="tour-step-counter">
                <Sparkles className="w-3.5 h-3.5" /> {stepIndex + 1} / {total}
              </span>
              <button onClick={() => skip(false)} className="text-xs text-white/40 hover:text-white/80 transition-colors underline underline-offset-4" data-testid="tour-skip-btn">
                {L('Passer', 'Skip')}
              </button>
            </div>
            <h4 className="relative font-outfit font-bold text-white text-lg mb-1">{en ? step.title.en : step.title.fr}</h4>
            <p className="relative text-sm text-white/70 leading-relaxed mb-4">{en ? step.desc.en : step.desc.fr}</p>

            <div className="relative h-1 w-full bg-white/10 rounded-full overflow-hidden mb-4">
              <motion.div className="h-full bg-primary rounded-full" animate={{ width: `${((stepIndex + 1) / total) * 100}%` }} transition={{ type: 'spring', damping: 20 }} />
            </div>

            <div className="relative flex items-center justify-between gap-2">
              <label className="inline-flex items-center gap-1.5 text-xs text-zinc-400 cursor-pointer select-none">
                <input
                  type="checkbox" checked={neverShow}
                  onChange={(e) => { setNeverShow(e.target.checked); tour.setNeverShow(e.target.checked); }}
                  className="w-3.5 h-3.5 rounded accent-primary"
                  data-testid="tour-never-show-checkbox"
                />
                {L('Ne plus afficher', "Don't show again")}
              </label>
              <div className="flex items-center gap-1.5 shrink-0">
                {stepIndex > 0 && (
                  <button onClick={prev} className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center text-white transition-colors" data-testid="tour-prev-btn">
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                )}
                <button onClick={next} className="inline-flex items-center gap-1 px-4 py-2 rounded-full bg-primary hover:bg-[#D50032] text-white text-sm font-bold shadow-[0_0_15px_rgba(255,23,68,0.3)] hover:shadow-[0_0_25px_rgba(255,23,68,0.5)] transition-all active:scale-95" data-testid="tour-next-btn">
                  {stepIndex + 1 === total ? L('Terminer', 'Finish') : L('Suivant', 'Next')} {stepIndex + 1 !== total && <ChevronRight className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>,
    document.body,
  );
};

export default TourSpotlight;
